import * as React from "react";

// OriginalSessionStore comes from uidispatcher
import { SessionStore as OriginalSessionStore } from "shared/src/uidispatcherapi/SessionStore";
import * as PropTypes from "prop-types";

import { connect } from "shared/src/stores/storeConnect";
import { PriceConfigStore } from "shared/src/stores/PriceConfigStore";
import { CartStore } from "../../../../../../stores/CartStore";
import { createLookupMap } from "shared/src/utils/CreateLookupMapUtil";
import ArticleCard from "articlecard/src/components/ArticleCard/ArticleCard";
import { BettyBundle } from "shared/src/types/EvaluateArticleNew";
import {
    ArticleCardCallbacks,
    ArticleCardDisplayConfig,
    CustomerTier,
    DebugInformation,
    SearchResultDebugInfo,
    SearchResultLtrDebugInfo
} from "shared/src/types/ArticleCardProps";
import { PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";

import { TrackingTags } from "shared/src/tracking/Tracking";
import { ArticleIdsRenderData, ListInformation, Bundle } from "shared/src/types/articlesearch/Article";
import { ShoppingCartPosition } from "shared/src/types/ShoppingCartPosition";
import { PriceConfig } from "shared/src/types/articlesearch/Price";
import { ReplaceInfo } from "../ReplaceListItem";

import { generateUuid } from "shared/src/utils/util";
import { isTouchScreen } from "shared/src/utils/isTouchScreen";
import { computePropsAndTrackBasketChange, getIsWeightPerPieceArticle } from "shared/src/tracking/BasketTrackingUtil";

import CartActions from "../../../../../../../app/actions/CartActions";

import { saveRecentSearches } from "../../../RecentSearches";

import BaseComponent from "../../../../../general/BaseComponent";
import StoreDataStore from "../../../../../../../app/stores/StoreDataStore";
import {
    categoryIdsExtractor,
    trackArticleView,
    trackOpenProductView
} from "../../../../../../../app/utils/ArticleTrackingUtil";
import CustomerHistoryAction from "../../../../../../../app/actions/CustomerHistoryAction";
import { getRewardIds } from "shared/src/utils/rewardArticleIds";
import { isFsdShop } from "shared/src/utils/ShopFlavor";
import {
    createEvaluateArticlePriceAndPromotionInfo,
    getStoreAvailability,
    getBasketIdsForBundle
} from "shared/src/utils/articleUtil";
import { ArticleRenderData } from "shared/src/types/RenderData";
import { calculateAmountForBasket } from "shared/src/utils/BasketUtil";
import { PageComponents } from "shared/src/tracking/PageComponents";
import { PageType } from "shared/src/types/PageType";

type Props = {
    // Data objects to display
    item: ArticleRenderData;
    replace: ReplaceInfo | undefined | null;
    articleDeprecated: boolean;
    nonExistentBundle: boolean;

    // For tracking
    articleIds: ArticleIdsRenderData;
    pageType: string;
    zeroResultType: string | undefined | null;
    inContainer: boolean;
    listInformation: ListInformation;
    marketplace: boolean;
    trackingInfo: TrackingTags;
    listPosition?: number;
    onList?: boolean;
    recentlyOrdered?: boolean;
    recentlyOrderedDateString?: string;
    // Inserted via stores:
    priceConfig: PriceConfig;
    storeName: string | undefined | null;
    locale: string;

    // Config stuff
    dontShowUnitPriceForBundle?: boolean | undefined;
    tabIndex?: number;

    // Add to Cart props
    cartAmounts: {
        [x: string]: number | undefined | null;
    };
    cartPrices: {
        [x: string]: ShoppingCartPosition | undefined | null;
    };
    cartEnabled: boolean;

    // SessioStore info
    selectedDeliveryAddress: {
        [x: string]: any;
    };
    isCustomer: boolean;
    isEmployee: boolean;
    loggedIn: boolean;
    selectedStoreId: string | undefined | null;
    customerTierLevel: CustomerTier | null | undefined;

    debugInfo: SearchResultDebugInfo | undefined | null;
    ltrDebugInfo: SearchResultLtrDebugInfo | undefined | null;
    solrScore: number | null | undefined;
    isMarioCountry: boolean;
};

export class DisconnectedNewArticleCardWrapper extends BaseComponent<Props> {
    static contextTypes = {
        router: PropTypes.object.isRequired
    };

    getBundles = () =>
        Object.values(this.props.item.variant?.bundles ? this.props.item.variant?.bundles : {}).reduce(
            (collection, bundle) => {
                const bettyBundleId = bundle?.bettyBundleId?.bettyBundleId;
                if (bettyBundleId) {
                    collection[bettyBundleId] = bundle;
                }
                return collection;
            },
            {} as { [key: string]: BettyBundle }
        );

    getPriceAndPromotionInfos = () => {
        const bundles = this.getBundles();
        const currentDnrNumbers = CartStore.getCartCurrentDnrNumbers();
        return Object.keys(bundles).reduce((collection, bettyBundleId) => {
            const appliedDnrs = CartStore.getPricesWithDnrInCart();
            if (bettyBundleId) {
                const ids = {
                    selectedBundleId: bettyBundleId,
                    bettyOfferId: null,
                    originalBundleId: null
                };
                collection[bettyBundleId] = createEvaluateArticlePriceAndPromotionInfo(
                    this.props.item,
                    ids,
                    this.props.selectedStoreId,
                    {
                        [bettyBundleId]: this.props.cartAmounts[bettyBundleId]
                    },
                    this.props.cartPrices[bettyBundleId],
                    currentDnrNumbers,
                    appliedDnrs
                );
            }
            return collection;
        }, {} as { [key: string]: PriceAndPromotionInfo });
    };

    callbacks: ArticleCardCallbacks = {
        onAddToListCreateNewList: () => saveRecentSearches(),

        onListMenuItemSelect: () => saveRecentSearches(),

        onAddToCart: (eventId: string, amountAdded: number, bettyBundleId: string | null | undefined) => {
            if (bettyBundleId) {
                const selectedBundle = this.getBundles()[bettyBundleId];
                const isWeightArticle = selectedBundle?.isWeightArticle === "WEIGHT";
                const weightPerPiece = {
                    unit: selectedBundle?.weightPerPiece?.uom,
                    value: selectedBundle?.weightPerPiece?.value
                };
                const weight = selectedBundle?.weightPerPiece?.value || null;

                const correctDelta = calculateAmountForBasket(amountAdded, isWeightArticle, weightPerPiece);
                const amountInBasketBeforeClick = this.props.cartAmounts[bettyBundleId] || 0;
                if (amountInBasketBeforeClick) {
                    CartActions.changeAmountInCart(bettyBundleId, correctDelta);
                } else {
                    CartActions.setItemInCart(bettyBundleId, correctDelta);
                }
                const correctTrackedValueAfterBasketAdd = correctDelta + amountInBasketBeforeClick;
                computePropsAndTrackBasketChange(
                    {
                        correctDelta,
                        originEventId: eventId,
                        value: correctTrackedValueAfterBasketAdd,
                        amount: amountAdded,
                        bettyBundleId,
                        trackingId: this.props.articleIds.trackingId,
                        articleDeprecated: this.props.articleDeprecated,
                        cartEnabled: this.props.cartEnabled,
                        isLoggedIn: this.props.loggedIn,
                        isEmployee: this.props.isEmployee,
                        selectedStoreId: this.props.selectedStoreId,
                        trackingInfo: this.props.trackingInfo,
                        inContainer: this.props.inContainer,
                        recentlyOrdered: this.props.recentlyOrdered,
                        recentlyOrderedDateString: this.props.recentlyOrderedDateString,
                        pageType: this.props.pageType,
                        zeroResultType: this.props.zeroResultType,
                        amountBefore: amountInBasketBeforeClick,

                        trackingTags: {
                            originEventId: eventId,
                            pageComponent: PageComponents.ARTICLE_CARD,
                            value: correctTrackedValueAfterBasketAdd,
                            position: undefined,
                            // Page Type could also be Category or Promotions, but most likely it's Search
                            pageType: (this.props.pageType || "SEARCH") as PageType,
                            isEmployee: this.props.isEmployee,
                            loggedIn: this.props.loggedIn ? "logged-in" : "not-logged-in",
                            bettyBundleId: bettyBundleId,
                            description: selectedBundle.description,
                            imageUrl: undefined,
                            brand: selectedBundle.brandName,
                            variantText: selectedBundle.variantText,
                            customerDisplayId: selectedBundle.customerDisplayId,
                            isWeightArticle,
                            isWeightPerPieceArticle: getIsWeightPerPieceArticle(
                                isWeightArticle,
                                selectedBundle.weightPerPiece
                            ),
                            amountInBasket: correctTrackedValueAfterBasketAdd,
                            ownBrand: undefined,
                            trackingId: this.props.articleIds.trackingId,
                            price: undefined,
                            strokePrice: undefined,
                            categoryPath: categoryIdsExtractor(selectedBundle),
                            weight
                        }
                    },
                    (bettyBundleId: string) => this.getBundles()[bettyBundleId],
                    (bettyBundleId) => this.getPriceAndPromotionInfos()[bettyBundleId]
                );
                saveRecentSearches();
            }
        },

        onArticleOpen: (
            clickedElement: string,
            eventId: string,
            bettyBundleId: string | null | undefined,
            optionalTrackingTags?: Partial<TrackingTags>
        ) => {
            if (bettyBundleId) {
                const selectedBundle = this.getBundles()[bettyBundleId];
                trackOpenProductView(
                    eventId,
                    bettyBundleId || "",
                    selectedBundle?.trackingInfo?.trackingId || this.props.articleIds.trackingId,
                    this.props.pageType,
                    this.props.zeroResultType,
                    this.props.inContainer,
                    clickedElement,
                    false,
                    null,
                    this.props.trackingInfo,
                    optionalTrackingTags
                );
                CustomerHistoryAction.loadArticles.trigger();
                saveRecentSearches();
            }
        },

        onSuccessfulImageLoad: (
            eventId: string,
            description: string | undefined,
            imageType: string,
            bettyBundleId: string | undefined
        ) => {
            if (bettyBundleId) {
                const selectedBundle = this.getBundles()[bettyBundleId];
                const selectedStoreData =
                    selectedBundle?.stores && this.props.selectedStoreId
                        ? selectedBundle?.stores[this.props.selectedStoreId]
                        : null;
                const availabilityInfo = getStoreAvailability(
                    selectedBundle as Bundle,
                    selectedStoreData,
                    this.props.isEmployee
                );
                const replacementLinkShown = false;
                const rewardIds = getRewardIds(this.getPriceAndPromotionInfos()[bettyBundleId], bettyBundleId);
                const onList = false; // FIXME (DaBi): Don't hardcode this anymore
                trackArticleView(
                    eventId,
                    bettyBundleId,
                    selectedBundle?.trackingInfo?.trackingId || this.props.articleIds.trackingId,
                    description,
                    imageType,
                    this.props.pageType,
                    this.props.zeroResultType,
                    this.props.inContainer,
                    onList,
                    this.props.recentlyOrdered || false,
                    availabilityInfo.availability,
                    this.props.marketplace,
                    (selectedBundle?.offers && selectedBundle?.offers[selectedBundle?.selectedOffer]?.offerId) ||
                        this.props.articleIds.offerId,
                    this.props.trackingInfo,
                    replacementLinkShown,
                    this.props.solrScore,
                    this.props.articleIds.childBundleIds,
                    rewardIds,
                    undefined
                );
            }
        }
    };

    render() {
        const isReplacementSearch = !!this.props.replace;

        const optionalTrackingTags: Partial<TrackingTags> = {
            trackingId: this.props.articleIds.trackingId,
            zeroResultType: this.props.zeroResultType,
            inContainer: this.props.inContainer || false,
            ownBrand: this.props.trackingInfo?.ownBrand,
            ropoRank: this.props.trackingInfo?.ropoRank,
            bettyVariantId: this.props.item?.variantId?.bettyVariantId,
            bettyVariantBundleIds: Object.keys(this.getBundles())
        };

        const displayConfig: ArticleCardDisplayConfig = {
            addToListDefaultGloballyVisible: true,
            isCustomer: this.props.isCustomer,
            isEmployee: this.props.isEmployee,
            isLoggedIn: this.props.loggedIn,
            customerTier: this.props.customerTierLevel,
            selectedStoreId: this.props.selectedStoreId,
            isReplacementSearch: isReplacementSearch,
            nonExistentBundle: this.props.nonExistentBundle,
            configuredPrimaryPrice: this.props.priceConfig.priceCompact.primary,
            configuredSecondaryPrice: this.props.priceConfig.priceCompact.secondary,
            primaryPriceType: this.props.priceConfig.priceBreakdown.primaryPriceType,
            includeMarginTaxes: this.props.priceConfig.priceBreakdown.includeMarginTaxesInPrice,
            dontShowUnitPriceForBundle: this.props?.dontShowUnitPriceForBundle,
            location: this.context.router.getCurrentLocation(),
            recentlyOrdered: !!this.props.recentlyOrdered,
            recentlyOrderedDateString: this.props.recentlyOrderedDateString,
            isTouchScreen: isTouchScreen(),
            locale: this.props.locale,
            storeName: this.props.storeName,
            isFsdShop: isFsdShop(),
            isMarioCountry: this.props.isMarioCountry,
            pageType: this.props.pageType
        };

        const basketInfo = {
            amountById: this.props.cartAmounts,
            tabIndex: this.props.tabIndex,
            enabled: this.props.cartEnabled
        };

        const debugInformation: DebugInformation = {
            debugInfo: this.props.debugInfo,
            ltrDebugInfo: this.props.ltrDebugInfo
        };

        return (
            <ArticleCard
                variant={this.props.item.variant}
                preselectedBundleId={this.props.item.selectedBundle}
                callbacks={this.callbacks}
                // This default seems to have always been implicit
                articleDeprecated={this.props.articleDeprecated}
                selectedDeliveryAddress={this.props.selectedDeliveryAddress}
                displayConfig={displayConfig}
                eventUuidGenerator={generateUuid}
                priceAndPromotionInfos={this.getPriceAndPromotionInfos()}
                basketInfo={basketInfo}
                debugInformation={debugInformation}
                optionalTrackingTags={optionalTrackingTags}
            />
        );
    }
}

export const NewArticleCardWrapper = connect(
    {
        stores: [
            [
                OriginalSessionStore,
                (store: typeof OriginalSessionStore) => ({
                    selectedDeliveryAddress: store.getSelectedDeliveryAddress(),
                    isCustomer: store.isCustomer(),
                    isEmployee: store.isEmployee(),
                    loggedIn: store.loggedIn(),
                    selectedStoreId: store.getCustomerStore(),
                    customerTierLevel: store.getCustomerTierLevel() as CustomerTier | null | undefined,
                    locale: store.getLocale(),
                    isMarioCountry: store.isMarioCountry()
                })
            ],
            [PriceConfigStore, (store: typeof PriceConfigStore) => ({ priceConfig: store.getConfig() })],
            [
                CartStore,
                (store: typeof CartStore, props: Props) => {
                    const basketArticleIds = Object.values(props?.item?.variant?.bundles ?? {})
                        .map(getBasketIdsForBundle)
                        .reduce((ids, arr) => ids.concat(arr), []);
                    const cartAmounts = createLookupMap(basketArticleIds, (id) => store.getItemAmount(id));
                    const cartPrices = createLookupMap(basketArticleIds, (id) => store.getCartItemTotalNetPrice(id));
                    return {
                        cartAmounts,
                        cartPrices,
                        cartEnabled: store.isEnabled()
                    };
                }
            ],
            [StoreDataStore, (storeData: typeof StoreDataStore) => ({ storeName: storeData?.getCurrentStoreName() })]
        ]
    },
    DisconnectedNewArticleCardWrapper
);
